import React, { useState, useEffect } from 'react';
import { Select, Button, Toast, Spin, Space, Typography } from '@douyinfe/semi-ui';
import { IconQrCode } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import LinkSelector from '../../components/LinkSelector';
import { useLocation } from 'react-router-dom';

const QrCodeGenerator = () => {
    const [loading, setLoading] = useState(false);
    const [applets, setApplets] = useState([]);
    const [qrCodeImage, setQrCodeImage] = useState('');
    const [selectedApplet, setSelectedApplet] = useState(null);
    const [path, setPath] = useState('');
    const [envVersion, setEnvVersion] = useState('release'); // 默认正式版
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const uid = queryParams.get('uid');
        if (uid) {
            fetchApplets(uid);
        } else {
            fetchApplets();
        }
    }, [location.search]);

    const fetchApplets = async (uid = null) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/weixin/list_weixin_public/', {
                params: {
                    public_type: 8, // 微信小程序
                    limit: 100, // 获取所有小程序，可根据实际情况调整
                    uids: uid // Add uid to filter applets if provided
                }
            });
            if (response.errno === 0) {
                const fetchedApplets = response.list || [];
                setApplets(fetchedApplets);
                if (uid && fetchedApplets.length > 0) {
                    const preselectedApplet = fetchedApplets.find(item => item.uid == uid);
                    if (preselectedApplet) {
                        setSelectedApplet(preselectedApplet);
                    }
                } else if (!uid && fetchedApplets.length > 0) {
                    setSelectedApplet(fetchedApplets[0]);
                }
            } else {
                Toast.error(response.errstr || '获取小程序列表失败');
            }
        } catch (error) {
            console.error('获取小程序列表失败:', error);
            Toast.error('获取小程序列表失败');
        } finally {
            setLoading(false);
        }
    };

    const generateQrCode = async () => {
        setLoading(true);
        setQrCodeImage('');
        try {
            if (!selectedApplet?.uid) {
                Toast.error('请选择小程序');
                setLoading(false);
                return;
            }
            const response = await request.get('/go/sp/weixin/xcx_qrcode/', {
                params: {
                    uid: selectedApplet.uid,
                    path: path,
                    env_version: envVersion,
                    is_hyaline: 1,
                    width: 430
                },
                responseType: 'arraybuffer' // Important for image data
            });

            if (response.byteLength > 0) {
                const base64Image = btoa(
                    new Uint8Array(response).reduce(
                        (data, byte) => data + String.fromCharCode(byte),
                        ''
                    )
                );
                setQrCodeImage(`data:image/png;base64,${base64Image}`);
                Toast.success('二维码生成成功');
            } else {
                Toast.error('获取二维码失败');
            }
        } catch (error) {
            console.error('生成二维码失败:', error);
            Toast.error('生成二维码失败');
        } finally {
            setLoading(false);
        }
    };

    const handleAppletChange = (value) => {
        const applet = applets.find(item => item.uid === value);
        setSelectedApplet(applet);
    };

    return (
        <div style={{ padding: '20px' }}>
            <Typography.Title heading={3}>生成小程序码</Typography.Title>
            <div style={{ maxWidth: '500px', marginTop: '20px' }}>
                <div className="semi-form-field semi-form-field-select semi-form-field-required">
                    <div className="semi-form-field-label">
                        <label className="semi-form-field-label-text">选择小程序</label>
                    </div>
                    <Select
                        placeholder="请选择小程序"
                        loading={loading}
                        onChange={handleAppletChange}
                        value={selectedApplet?.uid}
                    >
                        {applets.map(applet => (
                            <Select.Option key={applet.uid} value={applet.uid}>
                                {applet.public_name} ({applet.app_id})
                            </Select.Option>
                        ))}
                    </Select>
                    {!selectedApplet?.uid && <div className="semi-form-field-error-message">请选择小程序</div>}
                </div>
                <div style={{ marginBottom: '20px' }} />
                <LinkSelector
                    label="小程序页面路径"
                    placeholder="请输入页面路径，例如：pages/index/index"
                    onChange={setPath}
                    value={path}
                />
                <div style={{ marginBottom: '20px' }} />
                <div className="semi-form-field semi-form-field-select">
                    <div className="semi-form-field-label">
                        <label className="semi-form-field-label-text">小程序版本</label>
                    </div>
                    <Select
                        placeholder="选择小程序版本"
                        value={envVersion}
                        onChange={setEnvVersion}
                        style={{ width: '100%' }}
                    >
                        <Select.Option value="release">正式版 (release)</Select.Option>
                        <Select.Option value="trial">体验版 (trial)</Select.Option>
                        <Select.Option value="develop">开发版 (develop)</Select.Option>
                    </Select>
                </div>
                <div style={{ marginBottom: '20px' }} />
                <Button type="primary" onClick={generateQrCode} loading={loading} icon={<IconQrCode />}>
                    生成小程序码
                </Button>
                <Typography.Text type="quaternary" style={{ marginTop: '10px', display: 'block' }}>
                    注意：需要在小程序上线后才能使用生成小程序码功能
                </Typography.Text>
            </div>

            {qrCodeImage && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <img src={qrCodeImage} alt="小程序码" style={{ width: '200px', height: '200px', border: '1px solid #eee' }} />
                </div>
            )}
        </div>
    );
};

export default QrCodeGenerator;